import { mapDerivationApiToDerivation } from '@/pages/SILA/adapters/derivation.adapter';
import createBaseQueryWithReauth from '@/services/baseQueryWithReauth';
import { NewDerivation } from '@/pages/SILA/interfaces/NewDerivation';
import { UpdateDerivation } from '../interfaces/UpdateDerivation';
import { DerivationsFilter } from '../interfaces/DerivationProps';
import { Derivation } from '@/pages/SILA/interfaces/Derivation';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { API_BOT_URL } from '@/utils/servers';

const derivationsService = createApi({
  reducerPath: 'derivationsService',
  baseQuery: createBaseQueryWithReauth(API_BOT_URL),
  tagTypes: ['Derivation', 'Derivations', 'Comments'],
  endpoints: (builder) => ({
    getDerivation: builder.query<Derivation, string>({
      query: (derivationId) => `/derivations/${derivationId}`,
      transformResponse: (response: Derivation) =>
        mapDerivationApiToDerivation(response),
      providesTags: ['Derivation'],
    }),
    getDerivations: builder.query<
      PaginatedResponse<Derivation>,
      { filters: DerivationsFilter; paginated: PaginatedRequest }
    >({
      query: ({ filters, paginated }) => {
        const params = new URLSearchParams();

        if (filters?.user) params.append('user', filters.user);
        if (filters?.caseId) params.append('caseId', filters.caseId);
        if (filters?.status) params.append('status', filters.status.toString());
        if (filters?.dateFrom)
          params.append('scheduledFrom', filters.dateFrom.toISOString());
        if (filters?.dateTo)
          params.append('scheduledTo', filters.dateTo.toISOString());

        const offset = paginated?.page ?? 0;
        const limit = paginated?.pageSize ?? 10;

        params.append('offset', String(offset));
        params.append('limit', String(limit));

        return `/derivations?${params.toString()}`;
      },
      transformResponse: (response: PaginatedResponse<Derivation>) => ({
        ...response,
        items: response.items.map(mapDerivationApiToDerivation),
      }),
      providesTags: ['Derivations'],
    }),

    createDerivation: builder.mutation<Derivation, NewDerivation>({
      query: (data) => ({
        url: '/derivations',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Derivations'],
    }),

    updateDerivation: builder.mutation<
      Derivation,
      { derivationId: string; data: UpdateDerivation }
    >({
      query: ({ derivationId, data }) => ({
        url: `/derivations/${derivationId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Derivation', 'Derivations'],
    }),

    deleteDerivation: builder.mutation<void, string>({
      query: (derivationId) => ({
        url: `/derivations/${derivationId}/discard`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Derivation', 'Derivations'],
    }),

    sendMessage: builder.mutation<void, { message: string; sender: string }>({
      query: (body) => ({
        url: `/agent/send-message`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Derivation', 'Derivations'],
    }),

    getComments: builder.query<any[], string>({
      query: (derivationId) => `/derivations/${derivationId}/comments`,
      providesTags: ['Comments'],
    }),

    createComment: builder.mutation<
      void,
      { derivationId: string; content: string }
    >({
      query: ({ derivationId, content }) => ({
        url: `/derivations/${derivationId}/comments`,
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: ['Comments'],
    }),
  }),
});

export default derivationsService;

export const {
  useGetDerivationQuery,
  useGetDerivationsQuery,
  useCreateDerivationMutation,
  useUpdateDerivationMutation,
  useDeleteDerivationMutation,
  useSendMessageMutation,
  useGetCommentsQuery,
  useCreateCommentMutation,
} = derivationsService;
